import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('keydown', this.#onMenuKeyDown);
    this.addEventListener('focusout', this.#onMenuFocusOut);
    this.addEventListener('click', this.#onMoreClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('keydown', this.#onMenuKeyDown);
    this.removeEventListener('focusout', this.#onMenuFocusOut);
    this.removeEventListener('click', this.#onMoreClick);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#positionOverflow();
  }, 100);

  // Use the measured trigger and host, so edge clamping also survives resize/sticky headers.
  #positionOverflow() {
    // Header children may hydrate after connectedCallback; initialize lazily once per item.
    this.querySelectorAll('.menu-list__list-item > .menu-list__submenu').forEach((submenu) => {
      if (submenu.parentElement.querySelector(':scope > .tv-overflow-toggle')) return;
      const link = submenu.parentElement.querySelector(':scope > a');
      if (!link) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tv-overflow-toggle';
      button.setAttribute('aria-label', link.textContent.trim());
      button.setAttribute('aria-expanded', 'false');
      button.textContent = '⌄';
      submenu.before(button);
    });
    const host = this.refs.overflowMenu;
    const button = this.querySelector('[slot="more"] button');
    if (!host || !button) return;
    const trigger = button.getBoundingClientRect();
    const container = host.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    const left = Math.max(12, Math.min(trigger.left, window.innerWidth - width - 12));
    host.style.setProperty('--tv-overflow-left', `${left - container.left}px`);
    host.style.setProperty('--tv-overflow-top', `${trigger.bottom - container.top}px`);
    button.setAttribute('aria-haspopup', 'true');
    button.setAttribute('aria-expanded', String(this.dataset.overflowExpanded === 'true' && this.ariaExpanded === 'true'));
  }

  #onMoreClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('.tv-overflow-toggle');
    if (toggle) {
      const li = toggle.parentElement;
      const open = !li.hasAttribute('data-tv-expanded');
      li.toggleAttribute('data-tv-expanded', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (!event.target.closest('[slot="more"] button')) return;
    // Focus/pointerenter already opens the list; a click/tap must leave it available.
    this.activate({ target: this.querySelector('[slot="more"]') });
    this.#positionOverflow();
  };

  // Leaving the overflow list with Tab must close it, even if the pointer stays inside.
  #onMenuFocusOut = (event) => {
    if (this.dataset.overflowExpanded !== 'true') return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    queueMicrotask(() => {
      if (!this.contains(document.activeElement)) this.#deactivate(this.#state.activeItem, true);
    });
  };

  #onMenuKeyDown = (event) => {
    if (event.key === 'Escape' && this.#state.activeItem) {
      event.preventDefault();
      const more = this.querySelector('[slot="more"] button');
      const overflow = this.dataset.overflowExpanded === 'true';
      if (overflow) more?.focus();
      this.#deactivate(this.#state.activeItem, true);
    } else if (event.key === 'ArrowDown' && event.target instanceof Element && event.target.closest('[slot="more"]')) {
      event.preventDefault();
      this.activate({ target: this.querySelector('[slot="more"]') });
      this.querySelector('[slot="overflow"] > a')?.focus();
    }
  };

  // Keep a diagonal pointer journey from the narrow trigger into its wider list
  // inside one measured corridor. This does not overlay other header controls.
  #inOverflowPointerRegion() {
    if (this.dataset.overflowExpanded !== 'true' || this.ariaExpanded !== 'true') return false;
    const trigger = this.querySelector('[slot="more"] button')?.getBoundingClientRect();
    const panel = this.overflowMenu?.getBoundingClientRect();
    if (!trigger || !panel || !panel.width) return false;
    const { x, y } = this.#lastPointer;
    if (y >= panel.top && y <= panel.bottom && x >= panel.left && x <= panel.right) return true;
    if (y < trigger.top || y > panel.top) return false;
    const progress = (y - trigger.top) / Math.max(1, panel.top - trigger.top);
    const left = trigger.left + (panel.left - trigger.left) * progress;
    const right = trigger.right + (panel.right - trigger.right) * progress;
    return x >= left && x <= right;
  }

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    // Torvanto: altijd bijwerken, óók zonder active item. De sluit-timer
    // her-checkt elementFromPoint op deze positie; liep de pointer vast op
    // de leave-positie dan sloot het menu ten onrechte.
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      // Ignore queued pointer reconciliation after keyboard closure or activation changes.
      if (!this.#state.activeItem) return;
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      if (this.dataset.overflowExpanded === 'true' && !this.#inOverflowPointerRegion() && !target.closest('header-menu')) {
        this.#deactivate();
        return;
      }
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Cancel any pending close: the pointer (re-)entered a menu item in time.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = !item.closest('[slot="overflow"], [slot="more"]');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    this.#positionOverflow();
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Delay the close so briefly crossing the gap between link and submenu
    // (or a stray pointerleave) does not collapse the dropdown. A pointerenter
    // on any menu item within this window cancels the close in `activate`.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      // Torvanto: her-check de echte pointerpositie vóór sluiten. Tijdens de
      // hoogte-transitie van de panel rapporteert de hit-test soms de link
      // of de underlay i.p.v. de panel; de pointer staat dan feitelijk GEWONNEN
      // in de submenu en het menu mag niet dicht. Dektr ook gaten in de panel.
      const { x, y } = this.#lastPointer;
      const el = document.elementFromPoint(x, y);
      const submenu = findSubmenu(this.#state.activeItem);
      const inSub = Boolean(el && ((submenu && submenu.contains(el)) || this.#state.activeItem?.contains(el)));
      window.__tvDebug && window.__tvDebug.push({ t: Date.now(), x, y, el: el ? el.tagName + '.' + (el.className || '').toString().slice(0, 25) : 'none', inSub, active: this.#state.activeItem ? this.#state.activeItem.querySelector('.menu-list__link')?.textContent.trim().slice(0, 10) : null });
      if (inSub) {
        return;
      }
      this.#deactivate();
    }, 600);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover') || this.#inOverflowPointerRegion())) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.#positionOverflow();

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // The overflow trigger opens its own list, not the first child mega menu.
    return element.querySelector('button');
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}


